import { Controller } from "@hotwired/stimulus";
import { initChameleon } from '../../../chameleon-config';
import chmln from '@chamaeleonidae/chmln';

const CHAMELEON_BANNER_SELECTORS = [
    '[id*="chmln"]',
    '[class*="chmln"]',
    '[id*="chameleon"]',
    '[class*="chameleon"]',
    'iframe[src*="chameleon"]',
    'iframe[src*="trychameleon"]',
].join(', ');

const CHAMELEON_BANNER_HEIGHT_VAR = '--chameleon-banner-height';

export default class extends Controller {
    static values = {
        awsEnv: String
    }

    isSupportedEnv() {
        return ['production', 'qa'].includes(this.awsEnvValue);
    }

    connect() {
        if (!this.isSupportedEnv()) {
            return;
        }
        this.initializeChameleon();
        this.boundTurboLoad = this.handleTurboLoad.bind(this);
        this.boundTurboBeforeVisit = this.handleTurboBeforeVisit.bind(this);
        document.addEventListener('turbo:load', this.boundTurboLoad);
        document.addEventListener('turbo:before-visit', this.boundTurboBeforeVisit);
        this.startBannerOffsetObserver();
    }

    disconnect() {
        if (this.boundTurboLoad) {
            document.removeEventListener('turbo:load', this.boundTurboLoad);
        }
        if (this.boundTurboBeforeVisit) {
            document.removeEventListener('turbo:before-visit', this.boundTurboBeforeVisit);
        }
        this.stopBannerOffsetObserver();
    }

    handleTurboLoad() {
        this.initializeChameleon();
    }

    handleTurboBeforeVisit(event) {
        const nextUrl = event?.detail?.url;
        this.initializeChameleon(nextUrl);
    }

    markInlineLoaderBootstrapped() {
        if (window.chmln && window.chmln.accountToken) {
            window.__chameleonInitialized = true;
        }
    }

    updateChameleonLocation(nextUrl = null) {
        if (!window.chmln) {
            return;
        }
        window.chmln.location = (nextUrl || window.location.href).toString();
    }

    refreshChameleon() {
        if (window.chmln && typeof window.chmln.refresh === 'function') {
            window.chmln.refresh();
            return;
        }
        if (typeof chmln.refresh === 'function') {
            chmln.refresh();
        }
    }

    initializeChameleon(nextUrl = null) {
        this.markInlineLoaderBootstrapped();
        if (!window.__chameleonInitialized) {
            initChameleon();
            window.__chameleonInitialized = true;
        }

        this.updateChameleonLocation(nextUrl);
        this.identifyVisitor();
        this.refreshChameleon();
    }

    startBannerOffsetObserver() {
        if (this.bannerMutationObserver) {
            return;
        }
        this.trackedBannerElements = new Set();
        this.boundUpdateBannerOffset = this.updateBannerOffset.bind(this);
        this.bannerResizeObserver = new ResizeObserver(this.boundUpdateBannerOffset);
        this.bannerMutationObserver = new MutationObserver(() => {
            this.refreshBannerTracking();
            this.updateBannerOffset();
        });
        this.bannerMutationObserver.observe(document.body, { childList: true, subtree: true });
        this.bodyAttrObserver = new MutationObserver(this.boundUpdateBannerOffset);
        this.bodyAttrObserver.observe(document.body, {
            attributes: true,
            attributeFilter: ['style', 'class'],
        });
        window.addEventListener('resize', this.boundUpdateBannerOffset);

        this.refreshBannerTracking();
        this.updateBannerOffset();
    }

    stopBannerOffsetObserver() {
        if (!this.bannerMutationObserver) {
            return;
        }
        this.bannerMutationObserver.disconnect();
        this.bannerMutationObserver = null;
        this.bodyAttrObserver.disconnect();
        this.bodyAttrObserver = null;
        this.bannerResizeObserver.disconnect();
        this.bannerResizeObserver = null;
        window.removeEventListener('resize', this.boundUpdateBannerOffset);
        this.boundUpdateBannerOffset = null;
        this.trackedBannerElements = null;
        document.documentElement.style.removeProperty(CHAMELEON_BANNER_HEIGHT_VAR);
    }

    refreshBannerTracking() {
        const found = new Set(document.body.querySelectorAll(CHAMELEON_BANNER_SELECTORS));

        found.forEach((el) => {
            if (!this.trackedBannerElements.has(el)) {
                this.bannerResizeObserver.observe(el);
                this.trackedBannerElements.add(el);
            }
        });

        this.trackedBannerElements.forEach((el) => {
            if (!found.has(el)) {
                this.bannerResizeObserver.unobserve(el);
                this.trackedBannerElements.delete(el);
            }
        });
    }

    isOverlayBanner(el) {
        let cur = el;
        while (cur) {
            const position = window.getComputedStyle(cur).position;
            if (position === 'fixed' || position === 'sticky' || position === 'absolute') {
                return true;
            }
            cur = cur.parentElement;
        }
        return false;
    }

    measureTopBannerHeight() {
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        const maxBannerHeight = viewportHeight > 0 ? viewportHeight * 0.4 : 200;
        let maxHeight = 0;

        this.trackedBannerElements.forEach((el) => {
            const rect = el.getBoundingClientRect();
            if (rect.height <= 0 || rect.height > maxBannerHeight) {
                return;
            }
            const style = window.getComputedStyle(el);
            if (style.display === 'none' || style.visibility === 'hidden') {
                return;
            }
            if (!this.isOverlayBanner(el)) {
                return;
            }
            const isAtTop = rect.top <= 4;
            const spansViewport = viewportWidth === 0 || rect.width >= viewportWidth * 0.6;
            if (isAtTop && spansViewport && rect.height > maxHeight) {
                maxHeight = rect.height;
            }
        });

        const inlinePaddingTop = parseFloat(document.body.style.paddingTop) || 0;
        const inlineMarginTop = parseFloat(document.body.style.marginTop) || 0;
        const bodyInlinePush = Math.max(inlinePaddingTop, inlineMarginTop);

        return Math.max(maxHeight, bodyInlinePush);
    }

    updateBannerOffset() {
        const height = this.measureTopBannerHeight();
        if (height > 0) {
            document.documentElement.style.setProperty(
                CHAMELEON_BANNER_HEIGHT_VAR,
                `${Math.ceil(height)}px`
            );
        } else {
            document.documentElement.style.removeProperty(CHAMELEON_BANNER_HEIGHT_VAR);
        }
    }

    identifyVisitor() {
        if (window.__chameleonVisitorIdentified) {
            return;
        }
        var uid;
        try {
            uid = document.cookie.match(/\bvisitor-uid=([a-z0-9-]+)(;|$)/)[1];
        } catch (e) {
            document.cookie = 'visitor-uid=' + (uid = Math.random().toString(36).substring(2)) + '; expires=Tue, Oct 13 2037 04:24:07 UTC; path=/;';
        }
        if (window.chmln && typeof window.chmln.identify === 'function') {
            window.chmln.identify(uid, { visitor: true });
        } else {
            chmln.identify(uid, { visitor: true });
        }
        window.__chameleonVisitorIdentified = true;
    }
}
